import { TypographyV3 } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import type { Dispatch, SetStateAction } from 'react';

import Motion from '@/app/components/Motion';

import type { PopHighlightsFeatureProps } from './PopHighlightsFeature';

export interface PopHighlightsFeatureTabsProps {
  readonly activeFeatureIndex: number;
  readonly features: PopHighlightsFeatureProps[];
  readonly onTabClick: Dispatch<SetStateAction<number>>;
}

export function PopHighlightsFeatureTabs({
  activeFeatureIndex,
  features,
  onTabClick,
}: PopHighlightsFeatureTabsProps): JSX.Element {
  const t = useTranslations();

  return (
    <div>
      <Motion
        initial={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
        whileInView={{ opacity: 1 }}
      >
        <TypographyV3
          className="mb-6 text-xs uppercase tracking-wider xl:text-base"
          Element="h3"
          weight="bold"
        >
          {t('pop_highlights_eyebrow')}
        </TypographyV3>
      </Motion>
      <ul className="flex flex-col gap-y-6">
        {features.map(({ Icon, id, title }, i) => (
          <li key={id}>
            <Motion
              initial={{ opacity: 0, x: -50 }}
              transition={{ delay: (i + 1) * 0.2, duration: 0.5 }}
              viewport={{ once: true }}
              whileInView={{ opacity: 1, x: 0 }}
            >
              <button
                className={cx(
                  'flex items-center gap-x-4 transition-opacity duration-300 xl:gap-x-5',
                  {
                    'opacity-1': activeFeatureIndex === i,
                    'opacity-30': activeFeatureIndex !== i,
                  },
                )}
                data-cy="pop_highlights_feature_tab"
                onClick={() => {
                  onTabClick(i);
                }}
              >
                <Icon height={20} width={20} />
                <TypographyV3
                  className="whitespace-nowrap text-2xl leading-none xxl:text-heading-lg"
                  color="inherit"
                  Element="span"
                >
                  {t(title)}
                </TypographyV3>
              </button>
            </Motion>
          </li>
        ))}
      </ul>
    </div>
  );
}
